import { Message } from '../components/ai-assistant/types';

import apiClient from './apiClient';
import { API_CONFIG } from '../config';

interface ChatGPTResponse {
  success: boolean;
  data: {
    response: string;
    usage?: {
      prompt_tokens: number;
      completion_tokens: number;
      total_tokens: number;
    };
  };
}

interface EnhancedAIResponse {
  success: boolean;
  response?: string;
  consolidated_response?: string;
  agents_used?: string[];
  confidence_score?: number;
  execution_time?: number;
  workflow_type?: string;
  enterprise_features?: {
    multi_agent_used: boolean;
    workflow_orchestration: boolean;
    legal_compliance_checked: boolean;
    audit_trail_created: boolean;
  };
  action_buttons?: Array<{
    label: string;
    action: string;
    data?: any;
  }>;
  error?: string;
  fallback_available?: boolean;
  error_id?: string;
}

export const getChatGPTResponse = async (
  message: string,
  history: Array<{ role: string; content: string }>
): Promise<string> => {
  try {
    const response = await apiClient.post<ChatGPTResponse>(API_CONFIG.ENDPOINTS.AI_CHAT_SIMPLE, {
      message,
      history,
    });

    if (!response.success) {
      throw new Error(response.data?.response || 'Failed to get response from AI');
    }
    return response.data.response;
  } catch (error) {
    console.error('Error sending message to ChatGPT:', error);
    throw new Error('Failed to send message to ChatGPT');
  }
};

export const sendMessageToChatGPT = async (message: string): Promise<string> => {
  try {
    const response = await apiClient.post<ChatGPTResponse>(API_CONFIG.ENDPOINTS.AI_CHAT_SIMPLE, {
      message,
    });

    if (!response.success) {
      throw new Error(response.data?.response || 'Failed to get response from AI');
    }
    return response.data.response;
  } catch (error) {
    console.error('Error sending message to ChatGPT:', error);
    throw new Error('Failed to send message to ChatGPT');
  }
};

// Enterprise-grade AI Assistant service
export const sendMessageToEnhancedAI = async (
  message: string,
  sessionId: string,
  userId: string,
  firmId: string,
  context?: any
): Promise<EnhancedAIResponse> => {
  try {
    // Unified Whisper gateway for text: normalize before sending to Agentic AI
    try {
      const normalizeResp = await apiClient.post<any>('/api/whisper/normalize', {
        text: message,
        sessionId,
        context,
      });
      if (normalizeResp?.success && typeof normalizeResp.text === 'string') {
        message = normalizeResp.text;
        // Emit language detection for UI badges
        try {
          const lang = (normalizeResp as any).language || 'auto';
          window.dispatchEvent(
            new CustomEvent('whisper-language-detected', { detail: { language: lang } })
          );
        } catch (_) {
          // no-op
        }
      }
    } catch (e) {
      // Soft-fail: proceed with original message if normalization fails
    }

    console.log(
      '🔍 aiAssistant.ts: Making request to:',
      `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AI_CHAT}`
    );
    console.log('🔍 aiAssistant.ts: API_CONFIG.BASE_URL value:', API_CONFIG.BASE_URL);
    console.log(
      '🔍 aiAssistant.ts: Full URL will be:',
      `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AI_CHAT}`
    );

    const response = await apiClient.post<EnhancedAIResponse>(API_CONFIG.ENDPOINTS.AI_CHAT, {
      message,
      sessionId,
      userId,
      firmId,
      context: {
        ...context,
        enterprise_mode: true,
        timestamp: new Date().toISOString(),
      },
    });

    if (!response.success) {
      throw new Error(response.error || 'Enterprise AI system error');
    }

    return response;
  } catch (error: any) {
    console.error('Enterprise AI Assistant error:', error);

    // Prefer Axios error response details if available
    let errorData: any = {};
    if (error && error.response && error.response.data) {
      errorData = error.response.data;
    } else {
      // Parse error response if it's a JSON string in message
      try {
        errorData = JSON.parse(error.message);
      } catch {
        errorData = { error: error.message };
      }
    }

    // Attempt silent session recovery on 401 before surfacing error
    if (
      (typeof errorData?.error === 'string' && /401|Unauthorized/i.test(errorData.error)) ||
      errorData?.code === 'AUTHENTICATION_REQUIRED'
    ) {
      try {
        await fetch('/api/auth/refresh-token', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
      } catch (_) {
        // ignore recovery errors
      }
    }

    // Enterprise-grade error handling with fallback
    if (errorData.details?.status === 503 || error.response?.status === 503) {
      throw new Error('Enterprise AI system temporarily unavailable - fallback mode activated');
    }

    if (error.code === 'ECONNABORTED') {
      throw new Error('Enterprise AI request timeout - please try again');
    }

    // Surface OpenAI configuration issues clearly
    if (error.response?.status === 401 && /invalid/i.test(String(errorData?.error))) {
      throw new Error(
        'OpenAI API key invalid or unauthorized. Please update the backend OPENAI_API_KEY and restart the server.'
      );
    }

    throw new Error(errorData.error || errorData.message || 'Enterprise AI system error');
  }
};

// Voice command processing
export const processVoiceCommand = async (
  audioBlob: Blob,
  sessionId: string,
  userId: string,
  firmId: string
): Promise<EnhancedAIResponse> => {
  try {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'voice-command.wav');
    formData.append('sessionId', sessionId);
    formData.append('userId', userId);
    formData.append('firmId', firmId);

    const response = await apiClient.uploadFile<EnhancedAIResponse>(
      API_CONFIG.ENDPOINTS.AI_VOICE_COMMAND,
      audioBlob as File,
      {
        sessionId,
        userId,
        firmId,
      }
    );

    if (!response.success) {
      throw new Error(response.error || 'Voice command processing failed');
    }

    return response;
  } catch (error: any) {
    console.error('Voice command processing error:', error);

    // Parse error response if it's a JSON string
    let errorData;
    try {
      errorData = JSON.parse(error.message);
    } catch {
      errorData = { error: error.message };
    }

    throw new Error(errorData.error || 'Voice command processing failed');
  }
};
